// Shared low-level helpers used across the V2 engine pipeline.

import type {
  ClientProfile,
  Discipline,
  DimensionAppliesTo,
  Model,
} from '../types.js';

export const EPSILON = 1e-6;

export function sigmoid(x: number): number {
  return 1.0 / (1.0 + Math.exp(-x));
}

/**
 * Evaluate a discipline's `activationConditions` (sigmoid budget gate + optional
 * geo bypass). Returns a 0..1 fraction.
 */
export function evaluateActivationConditions(
  disc: Discipline,
  profile: ClientProfile,
): number {
  const cond = disc.activationConditions;
  if (!cond) return 1.0;

  const totalBudget = profile.totalBudget || 0;
  const geoSel = profile.selections?.geo;
  if (
    typeof geoSel === 'string' &&
    Array.isArray(cond.geoBypass) &&
    cond.geoBypass.includes(geoSel)
  ) {
    return 1.0;
  }

  const ramp = cond.rampWindow;
  if (!ramp) return 1.0;
  const start = Number(ramp.startBudget) || 0;
  const full = Number(ramp.fullBudget) || start * 2 || 1;

  if (totalBudget <= start) return 0.0;
  if (totalBudget >= full) return 1.0;

  if (ramp.shape === 'linear') {
    return (totalBudget - start) / (full - start);
  }

  const mid = (start + full) / 2;
  const width = (full - start) / 4; // ≈4σ → smooth ramp
  return sigmoid((totalBudget - mid) / width);
}

/**
 * Aggregate maturity factor across all dimensions whose `appliesTo` matches.
 * Multiple dimensions can co-exist (e.g. legacy `omni` + new `enterprise_platform`),
 * so we multiply their factors together.
 */
export function maturityFactor(
  model: Model,
  profile: ClientProfile,
  appliesTo: DimensionAppliesTo | DimensionAppliesTo[],
): number {
  const accepted = Array.isArray(appliesTo) ? appliesTo : [appliesTo];
  const dims = (model.dimensions || []).filter((d) => accepted.includes(d.appliesTo));
  if (dims.length === 0) return 0;
  return dims.reduce((acc, dim) => {
    const sel = profile.selections?.[dim.id];
    const values = Array.isArray(sel) ? sel : sel ? [sel] : [];
    if (values.length === 0) return acc * 0;
    let dimMult = 1;
    for (const value of values) {
      const opt = (dim.options || []).find((o) => o.value === value);
      dimMult *= Number(opt?.factor) || 0;
    }
    return acc * dimMult;
  }, 1);
}